import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Send, FileText, CheckCircle, XCircle, Clock, AlertTriangle, Plus, Eye, RefreshCw, Filter, Printer } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import { PageHeader } from '@/components/PageHeader';
import SendCommunicationWizard, { scaricaPdfDaStampare } from '@/components/SendCommunicationWizard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800', icon: FileText },
  PUBLISHED: { label: 'Pubblicato', color: 'bg-blue-100 text-blue-800', icon: Eye },
  SENDING: { label: 'Invio in corso', color: 'bg-yellow-100 text-yellow-800', icon: RefreshCw },
  SENT: { label: 'Inviato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  ARCHIVED: { label: 'Archiviato', color: 'bg-stone-100 text-stone-800', icon: FileText },
};

const TYPE_CONFIG = {
  AVVISO: { label: 'Avviso', color: 'bg-blue-100 text-blue-800' },
  URGENTE: { label: 'Urgente', color: 'bg-red-100 text-red-800' },
  LAVORI: { label: 'Lavori', color: 'bg-orange-100 text-orange-800' },
  SCADENZA: { label: 'Scadenza', color: 'bg-purple-100 text-purple-800' },
  ASSEMBLEA: { label: 'Assemblea', color: 'bg-green-100 text-green-800' },
  DOCUMENTO: { label: 'Documento', color: 'bg-cyan-100 text-cyan-800' },
  ALTRO: { label: 'Altro', color: 'bg-gray-100 text-gray-800' },
};

export default function CommunicationsCenter() {
  const { condominiumId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextProtocol, setNextProtocol] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  // ?tipo=ASSEMBLEA arriva dalla pagina Assemblee («Invia le lettere di convocazione»)
  const [typeFilter, setTypeFilter] = useState(() => new URLSearchParams(window.location.search).get('tipo') || '');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showSendWizard, setShowSendWizard] = useState(false);
  const [printing, setPrinting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    comm_type: 'AVVISO',
    priority: 0,
    channels: ['APP', 'EMAIL'],
  });

  const fetchCommunications = useCallback(async () => {
    if (!token || !condominiumId) return;
    
    setLoading(true);
    try {
      let url = `${API_URL}/api/condominiums/${condominiumId}/communications-center?page=${page}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (typeFilter) url += `&comm_type=${typeFilter}`;
      if (yearFilter) url += `&year=${yearFilter}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCommunications(data.communications || []);
        setTotal(data.total || 0);
        setNextProtocol(data.next_protocol_preview || '');
      } else {
        toast.error('Errore nel caricamento delle comunicazioni');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [token, condominiumId, page, statusFilter, typeFilter, yearFilter]);

  useEffect(() => {
    fetchCommunications();
  }, [fetchCommunications]);

  // ?apri=<id> arriva dalla busta della pagina Assemblee: si apre la bozza e subito «Verifica e invia»
  useEffect(() => {
    const apri = new URLSearchParams(window.location.search).get('apri');
    if (!apri || !token || !condominiumId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/communications-center/${apri}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSelectedComm(data);
          setShowDetailDialog(true);
          if (data?.communication?.status === 'DRAFT') setShowSendWizard(true);
        } else {
          toast.error('Bozza di convocazione non trovata');
        }
      } catch (e) {
        toast.error('Errore nel caricamento della bozza');
      }
    })();
  }, [token, condominiumId]);

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Titolo e contenuto sono obbligatori');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/communications-center`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Comunicazione creata: ${data.protocol_number}`);
        setShowCreateDialog(false);
        setFormData({ title: '', content: '', comm_type: 'AVVISO', priority: 0, channels: ['APP', 'EMAIL'] });
        fetchCommunications();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella creazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setCreating(false);
    }
  };

  const viewDetail = async (comm, keepOpen = false) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/communications-center/${comm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSelectedComm(data);
        if (!keepOpen) setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  // PDF unico da stampare (registro di consegna + lettere) per una comunicazione già inviata
  const handlePrintPdf = async (commId) => {
    setPrinting(true);
    try {
      const info = await scaricaPdfDaStampare({ condominiumId, commId, token, body: {} });
      toast.success(`PDF pronto: ${info.letters || '?'} lettere`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPrinting(false);
    }
  };

  // lettera stampata consegnata a mano o spedita: si registra la consegna
  const handleDelivered = async (recipient) => {
    const note = window.prompt(`Consegna a ${recipient.person_name || ''}: nota (es. numero raccomandata, data, firma in portineria)`, '');
    if (note === null) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/communications-center/${recipient.communication_id}/recipients/${recipient.id}/delivered`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
      if (res.ok) {
        toast.success('Consegna registrata');
        viewDetail({ id: recipient.communication_id }, true);
        fetchCommunications();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Errore');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Centro Comunicazioni" subtitle="Invio e protocollo delle comunicazioni" />
      <div className="flex flex-wrap gap-2 justify-end">
          <ExportButton
            condominiumId={condominiumId}
            exportType="communications-center"
            title="Esporta"
            formats={['print', 'pdf', 'csv']}
            showArchive={true}
            filters={{ year: yearFilter, status: statusFilter, type: typeFilter }}
            token={token}
            variant="outline"
            customEndpoint={`/api/condominiums/${condominiumId}/communications-center/export`}
          />
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuova Comunicazione
          </Button>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{total}</div>
            <div className="text-sm text-gray-500">Totale</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">
              {communications.filter(c => c.status === 'SENT').length}
            </div>
            <div className="text-sm text-gray-500">Inviate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-yellow-600">
              {communications.filter(c => c.status === 'DRAFT').length}
            </div>
            <div className="text-sm text-gray-500">Bozze</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm font-mono text-gray-600">{nextProtocol}</div>
            <div className="text-sm text-gray-500">Prossimo Protocollo</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium">Filtri:</span>
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                {[2026, 2025, 2024, 2023].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={fetchCommunications}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Communications Table */}
      <Card>
        <CardHeader>
          <CardTitle>Registro Comunicazioni</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : communications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nessuna comunicazione trovata
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Protocollo</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Titolo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-center">Destinatari</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {communications.map(comm => {
                  const statusCfg = STATUS_CONFIG[comm.status] || STATUS_CONFIG.DRAFT;
                  const typeCfg = TYPE_CONFIG[comm.comm_type] || TYPE_CONFIG.ALTRO;
                  const StatusIcon = statusCfg.icon;
                  
                  return (
                    <TableRow key={comm.id} className="cursor-pointer hover:bg-gray-50" onClick={() => viewDetail(comm)}>
                      <TableCell className="font-mono text-sm">
                        {comm.protocol_number || '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(comm.published_at || comm.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge className={typeCfg.color}>{typeCfg.label}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {comm.title}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusCfg.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusCfg.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1 text-xs">
                          <span className="text-green-600">{comm.sent_count || 0}</span>
                          <span>/</span>
                          <span>{comm.total_recipients || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuova Comunicazione</DialogTitle>
            <DialogDescription>
              Protocollo: <span className="font-mono">{nextProtocol}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titolo *</Label>
              <Input
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                placeholder="Oggetto della comunicazione"
              />
            </div>
            <div>
              <Label>Contenuto *</Label>
              <Textarea
                value={formData.content}
                onChange={e => setFormData({...formData, content: e.target.value})}
                placeholder="Testo della comunicazione..."
                rows={5}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={formData.comm_type} onValueChange={v => setFormData({...formData, comm_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorità</Label>
                <Select value={formData.priority.toString()} onValueChange={v => setFormData({...formData, priority: parseInt(v)})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Normale</SelectItem>
                    <SelectItem value="1">Alta</SelectItem>
                    <SelectItem value="2">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Canali: APP (bacheca), EMAIL (posta ordinaria), PEC (posta certificata, alla PEC in anagrafica).
                Prima i canali erano fissi e la PEC non esisteva. */}
            <div>
              <Label>Canali di invio</Label>
              <div className="flex flex-wrap gap-4 mt-1 text-sm">
                {[['APP', 'Portale (bacheca)'], ['EMAIL', 'Email ordinaria'], ['PEC', 'PEC (posta certificata)']].map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(formData.channels || []).includes(k)}
                      onChange={e => setFormData({
                        ...formData,
                        channels: e.target.checked
                          ? [...(formData.channels || []), k]
                          : (formData.channels || []).filter(c => c !== k)
                      })}
                      data-testid={`canale-${k}`}
                    />
                    {lbl}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Email e PEC partono solo se il server di posta corrispondente è configurato; chi non ha l'indirizzo in anagrafica viene saltato e compare nelle ricevute.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Creazione...' : 'Crea Bozza'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-sm text-gray-500">{selectedComm?.communication?.protocol_number}</span>
              {selectedComm?.communication?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedComm && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="whitespace-pre-wrap text-sm">{selectedComm.communication.content}</p>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-5 gap-3">
                <div className="text-center p-3 bg-blue-50 rounded">
                  <div className="text-xl font-bold text-blue-600">{selectedComm.stats.total}</div>
                  <div className="text-xs text-gray-500">Totale</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded">
                  <div className="text-xl font-bold text-green-600">{selectedComm.stats.sent}</div>
                  <div className="text-xs text-gray-500">Inviati</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded">
                  <div className="text-xl font-bold text-red-600">{selectedComm.stats.failed}</div>
                  <div className="text-xs text-gray-500">Falliti</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-xl font-bold text-gray-600">{selectedComm.stats.skipped}</div>
                  <div className="text-xs text-gray-500">Saltati</div>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded">
                  <div className="text-xl font-bold text-amber-700">{selectedComm.stats.print_pending || 0}</div>
                  <div className="text-xs text-gray-500">Da consegnare</div>
                </div>
              </div>

              {/* Destinatari e ricevute */}
              {selectedComm.recipients?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Destinatari e ricevute ({selectedComm.recipients.length})</h4>
                  <div className="max-h-[300px] overflow-y-auto border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Unità</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Canale</TableHead>
                          <TableHead>Recapito</TableHead>
                          <TableHead>Stato</TableHead>
                          <TableHead>Note</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...selectedComm.recipients]
                          .sort((a, b) => (a.channel === 'APP') - (b.channel === 'APP') || (a.person_name || '').localeCompare(b.person_name || ''))
                          .map(r => (
                          <TableRow key={r.id} className={r.channel === 'APP' ? 'text-gray-500' : ''}>
                            <TableCell className="text-xs font-mono">{(r.units || []).join(', ')}</TableCell>
                            <TableCell className="text-sm">{r.person_name || r.email || '—'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{{ PRINT: 'Stampa', APP: 'Bacheca', EMAIL: 'Email' }[r.channel] || r.channel}{r.print_mode ? ` · ${r.print_mode === 'RACCOMANDATA' ? 'racc. A/R' : 'a mano'}` : ''}</Badge>
                            </TableCell>
                            <TableCell className="text-xs">{r.channel === 'PRINT' ? (r.address || 'portineria') : (r.email || '')}</TableCell>
                            <TableCell>
                              {r.status === 'SENT' && <CheckCircle className="h-4 w-4 text-green-500" />}
                              {r.status === 'FAILED' && <XCircle className="h-4 w-4 text-red-500" />}
                              {r.status === 'SKIPPED' && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
                              {r.status === 'QUEUED' && <Clock className="h-4 w-4 text-gray-400" />}
                            </TableCell>
                            <TableCell className="text-xs">
                              {r.channel === 'PRINT' && r.status === 'QUEUED' ? (
                                <Button variant="outline" size="sm" onClick={() => handleDelivered(r)} data-testid={`consegnata-${r.id}`}>Segna consegnata</Button>
                              ) : (r.error || r.delivery_note || (r.sent_at ? formatDate(r.sent_at) : ''))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Chiudi</Button>
            {selectedComm?.stats?.print > 0 && (
              <Button variant="outline" onClick={() => handlePrintPdf(selectedComm.communication.id)} disabled={printing}>
                <Printer className="h-4 w-4 mr-2" />{printing ? 'Preparo il PDF…' : 'PDF da stampare'}
              </Button>
            )}
            {selectedComm?.communication?.status === 'DRAFT' && (
              <Button onClick={() => setShowSendWizard(true)} data-testid="verifica-e-invia">
                <Send className="h-4 w-4 mr-2" />
                Verifica e invia
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Procedura guidata: anteprima -> canali per destinatario -> conferma -> invio con ricevute */}
      <SendCommunicationWizard
        open={showSendWizard}
        onOpenChange={(v) => {
          setShowSendWizard(v);
          if (!v && selectedComm?.communication?.id) viewDetail({ id: selectedComm.communication.id }, true);
        }}
        condominiumId={condominiumId}
        communication={selectedComm?.communication}
        token={token}
        onDone={() => fetchCommunications()}
      />
    </div>
  );
}
